import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { User, Settings, Sun, Moon, Monitor, LogOut, Eye, EyeOff } from 'lucide-react'
import { useAuth } from '@/hooks/use-auth'
import { useThemeStore } from '@/stores/themeStore'
import { useIncognito } from '@/hooks/use-incognito'
import { queryClient } from '@/lib/queryClient'
import { cn } from '@/lib/utils'

type SimpleTheme = 'light' | 'dark' | 'system'

const THEME_OPTIONS: { value: SimpleTheme; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Light Mode', icon: Sun },
  { value: 'dark', label: 'Dark Mode', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
]

function initials(name?: string, email?: string): string {
  if (name) {
    const parts = name.trim().split(/\s+/)
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    return parts[0].slice(0, 2).toUpperCase()
  }
  if (email) return email.slice(0, 2).toUpperCase()
  return 'U'
}

export default function UserMenu() {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const auth = useAuth()
  const { theme, setTheme } = useThemeStore()
  const { isIncognito, toggle } = useIncognito()

  const activeSimpleTheme: SimpleTheme =
    theme === 'light' || theme === 'dark' || theme === 'system' ? theme : 'system'

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleLogout = () => {
    queryClient.clear()
    auth.logout()
    setOpen(false)
  }

  const handleSettings = () => {
    navigate('/settings')
    setOpen(false)
  }

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-sm font-medium text-muted-foreground hover:bg-muted/80 transition-colors"
        aria-label="User menu"
      >
        <User className="h-4 w-4" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-56 rounded-md border border-border bg-popover shadow-lg z-50 overflow-hidden">
          {/* User info */}
          {auth.user && (
            <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
                {initials(auth.user.name, auth.user.email)}
              </div>
              <div className="min-w-0">
                {auth.user.name && (
                  <p className="truncate text-sm font-medium">{auth.user.name}</p>
                )}
                {auth.user.email && (
                  <p className="truncate text-xs text-muted-foreground">{auth.user.email}</p>
                )}
              </div>
            </div>
          )}

          {/* Settings */}
          <div className="py-1">
            <button
              onClick={handleSettings}
              className="flex w-full items-center gap-3 px-4 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              <Settings className="h-4 w-4" />
              Settings
            </button>
          </div>

          {/* Demo Mode */}
          <div className="border-t border-border py-1">
            <button
              onClick={() => { toggle(); setOpen(false) }}
              className={cn(
                'flex w-full items-center gap-3 px-4 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors',
                isIncognito && 'text-purple-700 font-medium'
              )}
            >
              {isIncognito ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              Demo Mode
              {isIncognito && (
                <span className="ml-auto rounded-full bg-purple-100 px-1.5 py-0.5 text-xs font-semibold text-purple-900">ON</span>
              )}
            </button>
          </div>

          {/* Theme options */}
          <div className="border-t border-border py-1">
            {THEME_OPTIONS.map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                onClick={() => setTheme(value)}
                className={cn(
                  'flex w-full items-center gap-3 px-4 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors',
                  activeSimpleTheme === value && 'font-medium'
                )}
              >
                {activeSimpleTheme === value ? (
                  <span className="h-1.5 w-1.5 rounded-full bg-foreground shrink-0 ml-0.5 mr-0.5" />
                ) : (
                  <span className="h-1.5 w-1.5 shrink-0 ml-0.5 mr-0.5" />
                )}
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>

          {/* Logout */}
          <div className="border-t border-border py-1">
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 px-4 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors"
            >
              <LogOut className="h-4 w-4" />
              Log out
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
